import React from 'react'
import { autocompleteClasses, chipClasses, type Components, outlinedInputClasses, type Theme } from '@mui/material'

import { ChevronDownIcon, CloseIcon } from './icons'

const AUTOCOMPLETE_TAG_HEIGHT = 20 // px, matches MuiChip's own `size="small"` height (see theme/chip.tsx)

export const MuiAutocomplete: Components<Theme>['MuiAutocomplete'] = {
    defaultProps: {
        clearIcon: <CloseIcon />,
        popupIcon: <ChevronDownIcon />,
    },
    styleOverrides: {
        root: ({ theme, ownerState }) => ({
            /**
             * Autocomplete hardcodes its own OutlinedInput padding at the same specificity as ours;
             * `&&` repeats the generated class to outrank it. Padding (not minHeight) is what drives
             * the row's height here, so it has to vary by size itself, matching `theme/inputs.tsx`'s
             * own small/medium heights (28/32px) via the same padding-to-line-height math.
             */
            [`&& .${outlinedInputClasses.root}`]: {
                padding: theme.spacing(ownerState.size === 'small' ? 1 : 1.5, 2),
            },
            /**
             * Only relevant once there's at least one tag (the `.MuiAutocomplete-tag` sibling requirement) -
             * a single-select/freeSolo field with no tags has nothing else to display its value, so its
             * input must stay full width and visible even while unfocused. With tags present, the input is
             * just a secondary "type to search" box that should get out of the tags' way (its own `flexGrow: 1`
             * would otherwise keep claiming layout room) once it's not actually being typed into.
             */
            [`&& .${outlinedInputClasses.root}:not(.${outlinedInputClasses.focused}) .${autocompleteClasses.tag} ~ .${autocompleteClasses.input}`]: {
                flexGrow: 0,
                width: 0,
                minWidth: 0,
                padding: 0,
            },
            [`&& .${outlinedInputClasses.root} .${autocompleteClasses.input}`]: {
                padding: theme.spacing(0, 1),
                height: AUTOCOMPLETE_TAG_HEIGHT,
            },
            /**
             * Selected-value tags render as MuiChip's own `primary` variant (see `renderMultipleValue` in
             * `common/Input/Autocomplete.tsx` and `theme/chip.tsx`) - this only tightens the line-height
             * Chip otherwise sets for its larger default use, so the label doesn't overflow this fixed tag height.
             * `pointerEvents: 'none'` makes the tag itself inert to hover (it's a value display, not an
             * affordance), while re-enabling it on the delete icon so that stays hoverable/clickable.
             */
            [`& .${autocompleteClasses.tag}.${chipClasses.root}`]: {
                lineHeight: 1,
                pointerEvents: 'none',

                [`& .${chipClasses.deleteIcon}`]: {
                    pointerEvents: 'auto',
                },
            },
            /**
             * All tags, including "+N more" text
             */
            [`& .${autocompleteClasses.tag}`]: {
                margin: 0,
            },
            /**
             * "+N more" text only (not actual chips)
             */
            [`& .${autocompleteClasses.tag}:not(.${chipClasses.root})`]: {
                ...theme.typography.body2,
                color: theme.palette.grey[500],
            },
            /**
             * "close" and "chevron" icons
             */
            [`& .${autocompleteClasses.clearIndicator}:hover, & .${autocompleteClasses.popupIndicator}:hover`]: {
                backgroundColor: 'transparent',
            },
        }),
        // matches theme/dropdown.ts's MuiMenu.styleOverrides.paper values
        paper: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.common.white,
            boxShadow: theme.shadows[4],
        }),
        listbox: ({ theme }) => ({
            padding: theme.spacing(1, 0),
        }),
        /**
         * `!important` because MUI's own `AutocompleteListbox` hardcodes a same-specificity
         * `[aria-disabled="true"]` rule (opacity ~0.38) that otherwise wins on injection order.
         */
        option: {
            '&[aria-disabled="true"]': {
                opacity: '0.5 !important',
            },
        },
    },
}
